import React from 'react';
import { JobDetails } from '../../../../services/applicant_tracking/JobService/EditJobDto';
import {
  DropdownActionButton,
  DropdownActionOption,
} from '../../../../components/DropdownActionButton';
import { SetupHeaderButtonType } from '../../../../components/SetupPageHeader';
import { SetupFooterButtonType } from '../../../../components/SetupPageFooter';

interface Args {
  jobDetails: JobDetails;
  isEditFlow: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onSaveAndContinue: () => void;
  onSaveAsDraft: () => void;
}

interface HeaderSaveSlot {
  customLastItem?: React.ReactNode;
  lastItem?: SetupHeaderButtonType;
}

interface FooterSaveSlot {
  customFirstItem?: React.ReactNode;
  firstItem?: SetupFooterButtonType;
}

interface Result {
  saveButtonText: string;
  headerSaveSlot: HeaderSaveSlot;
  footerSaveSlot: FooterSaveSlot;
}

export function buildJobWizardSaveActions({
  jobDetails,
  isEditFlow,
  disabled,
  isLoading,
  onSaveAndContinue,
  onSaveAsDraft,
}: Args): Result {
  const jobPublished = jobDetails?.status === 'published';
  const showDraftOption = !isEditFlow || jobDetails?.status === 'draft';
  const saveButtonText =
    isEditFlow && jobPublished ? 'Save and Publish' : 'Save and Continue';

  const dropdownOptions: DropdownActionOption[] = [
    {
      title: saveButtonText,
      description: 'Save your progress and continue to the next step.',
      action: onSaveAndContinue,
    },
    ...(showDraftOption
      ? [
          {
            title: 'Save as Draft',
            description:
              "The job setup isn't complete yet and won't be active until you finalise it.",
            action: onSaveAsDraft,
          },
        ]
      : []),
  ];

  const renderSaveDropdown = (direction?: 'up' | 'down') => (
    <DropdownActionButton
      label={saveButtonText}
      disabled={disabled}
      isLoading={isLoading}
      direction={direction}
      options={dropdownOptions}
    />
  );

  const fallbackHeaderButton: SetupHeaderButtonType = {
    title: saveButtonText,
    type: 'Save',
    disabled,
    isLoading,
    action: onSaveAndContinue,
  };

  const fallbackFooterButton: SetupFooterButtonType = {
    title: saveButtonText,
    type: 'Save',
    disabled,
    isLoading,
    action: onSaveAndContinue,
  };

  return {
    saveButtonText,
    headerSaveSlot: showDraftOption
      ? { customLastItem: renderSaveDropdown() }
      : { lastItem: fallbackHeaderButton },
    footerSaveSlot: showDraftOption
      ? { customFirstItem: renderSaveDropdown('up') }
      : { firstItem: fallbackFooterButton },
  };
}
